import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import useAuth from '../../hooks/useAuth';
import useForm from '../../hooks/useForm';
import { validateEmail, validateRequired } from '../../utils/validators';
import FormField from '../common/FormField';
import Alert from '../common/Alert';
import './LoginForm.css';

const LoginForm = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [submitError, setSubmitError] = React.useState('');

  const validate = (vals) => {
    const errs = {};
    const emailErr = validateEmail(vals.email);
    if (emailErr) errs.email = emailErr;
    const pwdErr = validateRequired(vals.password, 'Password');
    if (pwdErr) errs.password = pwdErr;
    return errs;
  };

  const { values, errors, handleChange, handleSubmit, isSubmitting } = useForm({ email: '', password: '' }, validate);

  const onSubmit = async (vals) => {
    setSubmitError('');
    try {
      const profile = await login(vals.email, vals.password);
      // Someone who registered choosing "Landlord / Property Manager" goes straight to setting up
      // their organisation instead of the generic applicant dashboard — see RegisterForm. A token that
      // still needs its MFA challenge ('verify') is shown by ProtectedRoute regardless of destination;
      // an account that has never enrolled ('setup') gets a one-time, skippable prompt instead.
      let intent = null;
      try { intent = localStorage.getItem('postRegisterIntent'); localStorage.removeItem('postRegisterIntent'); } catch (e) { /* best effort */ }
      const destination = intent === 'owner' ? '/get-started' : '/dashboard';
      if (profile?.mfa_status === 'setup') {
        navigate('/mfa-prompt', { state: { destination } });
      } else {
        navigate(destination);
      }
    } catch (err) {
      setSubmitError(err.response?.data?.message || 'Invalid email or password');
    }
  };

  return (
    <form className="login-form" onSubmit={handleSubmit(onSubmit)} noValidate>
      {submitError && <Alert type="error" message={submitError} dismissible onClose={() => setSubmitError('')} />}
      
      <FormField
        label="Email Address"
        name="email"
        type="email"
        value={values.email}
        onChange={handleChange}
        error={errors.email}
        required
      />
      
      <FormField
        label="Password"
        name="password"
        type="password"
        value={values.password}
        onChange={handleChange}
        error={errors.password}
        required
      />
      
      <div className="login-form__actions">
        <label className="login-form__remember">
          <input type="checkbox" name="remember" />
          <span>Remember me</span>
        </label>
        <Link to="/forgot-password" className="login-form__forgot">Forgot Password?</Link>
      </div>

      <button type="submit" className="login-form__submit" disabled={isSubmitting}>
        {isSubmitting ? 'Logging in...' : 'Login'}
      </button>

      <p className="login-form__register-link">
        Don't have an account? <Link to="/register">Register here</Link>
      </p>
    </form>
  );
};

export default LoginForm;
